import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { PageLayout } from "./components/Page/PageLayout";
import { TalentBranch } from "./components/TalentBranches/TalentBranch";
import { YoutubeVideo } from "./components/Videos/YoutubeVideo";
import { Section } from "./components/Page/Section";
import { SectionHeader } from "./components/Page/SectionHeader";
import { Branches, ToastFormat, ToastType } from "./shared/enums";
import { TalentBranch as TalentBranchEntity } from "./generated/graphql";
import { Text } from "./components/shared/typography";
import { NewsArticle } from "./components/News/NewsArticle";
import { NewsArticle as NewsArticleType } from "./shared/newsArticle";
import { ApolloError, gql, useLazyQuery, useMutation } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import { KeycloakAccessToken } from "./lib/keycloakAccessToken";
import { Partners } from "./components/Partners/partners";
import { YoutubeVideo as YoutubeVideoEntity } from "./shared/youtubeVideo";
import VideoModal from "./components/Videos/VideoModal";
import { useLocation } from "react-router-dom";
import { getServiceTag } from "./lib/getServiceTag";
import { ActivityPreview } from "./components/VistActivity/ActivityPreview";
import { useToast } from "./lib/useToast";
import { getVoucherCodeErrorMessage } from "./lib/getErrorMessage";
import Events from "./components/Events/Events";
import Applications from "./components/Applications/Applications";
import { getClientVersion } from "./lib/getClientVersion";
import { isMld } from "./lib/isMld";

// ML2 Announcement Banner - Full width
const ML2BannerWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
  box-sizing: border-box;
`;

const ML2Banner = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  max-width: calc(1128px + 24px + 24px);
  margin: 0 auto;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary500} 0%, ${props => props.theme.colors.primary700} 100%);
  border-radius: 8px;
  text-decoration: none;
  color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.cardShadow};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.cardShadowHover};
  }
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.span`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 18px;
  font-weight: 700;
`;

const BannerSubtitle = styled.span`
  font-family: ${props => props.theme.fonts.body};
  font-size: 14px;
  opacity: 0.9;
`;

const BannerArrow = styled.span`
  font-size: 24px;
  margin-left: 16px;
`;

// Get ML2 URL based on current hostname
const getML2Url = (): string => {
  const hostname = window.location.hostname;
  
  // Map ML1 hostnames to ML2 URLs
  // EU Production (legacy ML1 domain)
  if (hostname === "live.mentice.com") {
    return "https://eu.live.mentice.com/v2/";
  }
  // US Production
  if (hostname === "us.live.mentice.com") {
    return "https://us.live.mentice.com/v2/";
  }
  // EU Staging
  if (hostname === "portal.mentice-staging.com") {
    return "https://portal.mentice-staging.com/v2/";
  }
  // US Staging
  if (hostname === "portal-us.mentice-staging.com") {
    return "https://portal-us.mentice-staging.com/v2/";
  }
  // EU Internal
  if (hostname === "internal.live.mentice.com") {
    return "https://internal.live.mentice.com/v2/";
  }
  // US Internal
  if (hostname === "internal-us.live.mentice.com") {
    return "https://internal-us.live.mentice.com/v2/";
  }
  
  // Development/localhost fallback
  return "https://portal.mentice-staging.com/v2/";
};

function Home() {
  const { keycloak } = useKeycloak();
  const user: KeycloakAccessToken | undefined = keycloak?.tokenParsed;
  const serviceTag = getServiceTag();
  const clientVersion = getClientVersion();

  const [, createToast] = useToast();

  const inviteCode = new URLSearchParams(useLocation().search).get(
    "inviteCode"
  );
  const [joinGroup, { data: joinGroupData }] = useMutation(
    gql`
      mutation AcceptInvite($code: String!) {
        acceptInvite(code: $code) {
          groupName
        }
      }
    `,
    {
      variables: { code: inviteCode },
      onCompleted(data) {
        createToast({
          title:
            "You've successfully joined the group " +
            data.acceptInvite.groupName,
          type: ToastType.SUCCESS,
          format: ToastFormat.BANNER,
        });
      },
      onError(err) {
        console.log(err);
        createToast({
          title: getVoucherCodeErrorMessage(
            err.graphQLErrors[0].extensions?.code
          ),
          type: ToastType.ERROR,
          format: ToastFormat.BANNER,
        });
      },
    }
  );

  useEffect(() => {
    if (
      inviteCode &&
      keycloak?.authenticated &&
      !inviteCode.startsWith("cmrav") &&
      !inviteCode.startsWith("cerav")
    ) {
      joinGroup();
    }
  }, [inviteCode, keycloak?.authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const [
    checkClientVersion,
    { data: clientVersionData, error: clientVersionError },
  ] = useLazyQuery(
    gql`
      query FetchMaxClientVersion {
        maxClientVersion {
          version
          links
        }
      }
    `,
    {
      onCompleted(data) {
        console.log(data);
        const versionTest = clientVersion.match(/\d+\.\d+\.\d+/g);
        if (data?.maxClientVersion.version && versionTest && versionTest.length > 0) {
          const [currentMajor, currentMinor, currentPatch] = versionTest[0].split('.').map((e:any) => parseInt(e))
          const [maxMajor, maxMinor, maxPatch] = data.maxClientVersion.version.split('.').map((e:any) => parseInt(e))

          console.log(maxPatch + " " + currentPatch)
          if (maxMajor > currentMajor ||
              maxMajor === currentMajor && maxMinor > currentMinor ||
              maxMajor === currentMajor && maxMinor === currentMinor && maxPatch > currentPatch) {
            createToast({
              title: "There is a newer version (" + data.maxClientVersion.version + ") of Mentice Live Desktop <BR/> Please download and run " + data.maxClientVersion.links.installer ,
              type: ToastType.ERROR,
              format: ToastFormat.UNSAFE,
            });
          }
        }
      }
    }
  );
  useEffect(() => {
    if (clientVersion) {
      checkClientVersion()
    }
  }, [clientVersion, checkClientVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  const voucherCode = new URLSearchParams(useLocation().search).get(
    "voucherCode"
  );
  const [acceptVoucher] = useMutation(
    gql`
      mutation AcceptVoucher($code: String!) {
        acceptVoucher(code: $code) {
          course
          groupName
          learningSpaceNames
        }
      }
    `,
    {
      variables: { code: voucherCode },
      onCompleted(data) {
        createToast({
          title:
            "You’ve successfully been added to the group " +
            data.acceptVoucher.groupName +
            " and now have access to the course " +
            data.acceptVoucher.course +
            " from " +
            data.acceptVoucher.learningSpaceNames.join(", "),
          type: ToastType.SUCCESS,
          format: ToastFormat.BANNER,
        });
        if (user) {
          fetchTalentBranches();
        }
      },
      onError(err: ApolloError) {
        console.log(JSON.stringify(err));
        createToast({
          title: getVoucherCodeErrorMessage(
            err.graphQLErrors[0].extensions?.code,
            err.graphQLErrors[0].extensions?.validTo
          ),
          type: ToastType.ERROR,
          format: ToastFormat.BANNER,
        });
      },
    }
  );

  useEffect(() => {
    if (voucherCode) {
      acceptVoucher();
    }
  }, [voucherCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loginCode = new URLSearchParams(useLocation().search).get(
    "qrlogin"
  );
  const [loginQr] = useMutation(
    gql`
      mutation QRLogin($payload: String!) {
        QRLogin(payload: $payload) {
          status
        }
      }
    `,
    {
      variables: { payload: loginCode },
      onCompleted(data) {
        console.log(data);
        if (data.QRLogin.status === 'ok') {
          createToast({
            title:
              "You've successfully logged in to simulator: " +
              data.QRLogin.status,
            type: ToastType.SUCCESS,
            format: ToastFormat.BANNER,
          });
        }
        else {
          createToast({
            title: 'Failed to login to simulator: ' + data.QRLogin.status,
            type: ToastType.ERROR,
            format: ToastFormat.BANNER,
          });
        }
      },
      onError(err: ApolloError) {
        console.log(JSON.stringify(err));
        createToast({
          title: 'Failed to login to simulator',
          type: ToastType.ERROR,
          format: ToastFormat.BANNER,
        });
      },
    }
  );

  useEffect(() => {
    if (loginCode) {
      loginQr();
    }
  }, [loginCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [
    fetchYouttubeVideos,
    { data: youtubeVideosData, error: youtubeVideosErrors },
  ] = useLazyQuery(
    gql`
      query FetchYoutubeVideos($maxResults: Int!) {
        youtubeVideos(maxResults: $maxResults) {
          title
          thumbnail
          id
        }
      }
    `,
    {
      variables: { maxResults: 4 },
    }
  );

  const [fetchNews, { data: newsData, error: newsErrors }] = useLazyQuery(
    gql`
      query FetchNewsArticles {
        newsArticles {
          title
          date
          link
          image
        }
      }
    `
  );

  const [
    fetchTalentBranches,
    { data: talentBranchesData, error: talentBranchesErrors },
  ] = useLazyQuery(
    gql`
      query FetchTalentBranches($emailAddress: String!) {
        enrolledTalentBranches(emailAddress: $emailAddress) {
          id
          code
          name
          description
          image
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      variables: { emailAddress: user?.email ?? "" },
    }
  );

  // Trigger request after user data loads
  useEffect(() => {
    if (user) {
      fetchTalentBranches();
      fetchYouttubeVideos();
      fetchNews();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      fetchTalentBranches();
    }
  }, [joinGroupData]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldDisplayTalentBranches =
    !talentBranchesErrors && talentBranchesData;

  let sortedBranches = [];
  if (
    talentBranchesData &&
    talentBranchesData.enrolledTalentBranches &&
    talentBranchesData.enrolledTalentBranches.length > 0
  ) {
    sortedBranches = [...talentBranchesData.enrolledTalentBranches].sort(
      (a: TalentBranchEntity, b: TalentBranchEntity) => {
        if (
          a.code === Branches.goto ||
          a.code === Branches.staging ||
          b.code === Branches.free
        ) {
          return -1;
        } else if (
          a.code === Branches.free ||
          b.code === Branches.goto ||
          b.code === Branches.staging
        ) {
          return 1;
        } else {
          return 0;
        }
      }
    );
  }

  const [selectedVideo, setSelectedVideo] = useState<string | undefined>(
    undefined
  );

  return (
    <PageLayout title="Mentice Live">
      {!isMld() && (
        <ML2BannerWrapper>
          <ML2Banner
            href={getML2Url()}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              window.location.href = getML2Url();
            }}
          >
            <BannerContent>
              <BannerTitle>✨ Try the upcoming Mentice Live 2</BannerTitle>
              <BannerSubtitle>For Vascular Twin users</BannerSubtitle>
            </BannerContent>
            <BannerArrow>→</BannerArrow>
          </ML2Banner>
        </ML2BannerWrapper>
      )}

      <SectionHeader title="Learning & Development">
        <Events
          showViewAllLink={true}
          maxEventsToShow={4}
          showActionBar={false}
        />
        <Section
          title="Learning Spaces"
          maxColumns={2}
          primaryActionText="View All"
          primaryActionLink="/learning-spaces"
        >
          {/* List branches */}
          {shouldDisplayTalentBranches &&
            sortedBranches &&
            sortedBranches.length > 0 &&
            sortedBranches.map((branch: TalentBranchEntity) => (
              <TalentBranch
                key={branch.id}
                talentBranchId={branch.id}
                talentBranchName={branch.code}
                code={branch.code}
                title={branch.name}
                description={branch.description}
                image={branch.image}
              />
            ))}
          {/* Show "enroll" prompt */}
          {shouldDisplayTalentBranches &&
            talentBranchesData.enrolledTalentBranches.length === 0 && (
              <Text>No learning spaces available</Text>
            )}
          {/* List errors */}
          {talentBranchesErrors &&
            talentBranchesErrors.graphQLErrors.map(
              (error: { message: string }, i: number) => (
                <Text key={i}>{error.message}</Text>
              )
            )}
          {/* Show loading message */}
          {!talentBranchesErrors && !talentBranchesData && (
            <Text>Loading Learning Spaces...</Text>
          )}
        </Section>
        <ActivityPreview key={user?.sub || ""} user={user} />
      </SectionHeader>
      {/* VIST modules and single-user cloud cases */}
      <Applications />

      {selectedVideo && (
        <VideoModal
          selectedVideo={selectedVideo}
          setSelectedVideo={() => setSelectedVideo(undefined)}
        />
      )}

      <SectionHeader title="News">
        {/* List the four newest YouTube videos */}
        {youtubeVideosData && !youtubeVideosErrors && (
          <Section
            title="Videos"
            maxColumns={4}
            primaryActionText="View All"
            primaryActionLink="/videos"
          >
            {!youtubeVideosErrors &&
              youtubeVideosData &&
              youtubeVideosData.youtubeVideos.map(
                (video: YoutubeVideoEntity, i: number) => (
                  <YoutubeVideo
                    key={i}
                    title={video.title}
                    id={video.id}
                    thumbnail={video.thumbnail}
                    setSelectedVideo={setSelectedVideo}
                  />
                )
              )}
          </Section>
        )}

        {/* Show loading message for YouTube Videos */}
        {!youtubeVideosErrors && !youtubeVideosData && (
          <Section title="Videos" maxColumns={1}>
            <Text>Loading YouTube Videos...</Text>
          </Section>
        )}

        {/* List YouTube video errors */}
        {((youtubeVideosErrors && !youtubeVideosData) ||
          (youtubeVideosErrors && youtubeVideosData)) && (
          <Section title="Videos" maxColumns={1}>
            {youtubeVideosErrors.graphQLErrors.map(
              (error: { message: string }, i: number) => (
                <Text key={i}>{error.message}</Text>
              )
            )}
          </Section>
        )}

        {!newsErrors && newsData && (
          <Section title="Feed" maxColumns={3}>
            {newsData.newsArticles.map(
              (article: NewsArticleType, i: number) => (
                <NewsArticle
                  key={i}
                  title={article.title}
                  date={article.date}
                  link={article.link}
                  image={article.image}
                  shouldOpenInNewTab={!serviceTag}
                />
              )
            )}
          </Section>
        )}
      </SectionHeader>

      {/* List of partner logos with link to website */}
      <Partners shouldOpenInNewTab={!serviceTag} />
    </PageLayout>
  );
}

export default Home;
